import Layout from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import CategoryShell from "@/components/feature/CategoryShell";
import CharacterCard from "@/components/base/CharacterCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useCategoryFilter } from "@/hooks/useCategoryFilter";
import { characterList } from "@/mocks/characters";
import type { Character } from "@/types";

const filterGroups = [
  {
    key: "anime",
    title: "按动画作品",
    options: [
      "星海启航",
      "苍穹之翼",
      "机械之心",
      "樱花剑姬",
      "时空旅人",
      "甜梦烘焙屋",
      "赛博都市 2030",
      "灵狐物语",
      "龙脊传说",
    ],
  },
  { key: "roleType", title: "按角色属性", options: ["主角", "配角"] },
];

const sortOptions = [
  {
    label: "最新",
    compare: (a: Character, b: Character) =>
      new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
  },
  { label: "热门", compare: (a: Character, b: Character) => b.views - a.views },
];

export default function CharacterCategoryPage() {
  usePageMeta({
    title: "角色介绍 - 风车动漫网",
    description:
      "风车动漫网角色介绍频道整理各作品角色的背景故事、作品关系与代表台词，支持按动画作品与角色属性筛选，深入了解你喜欢的角色。",
    keywords: "角色介绍,动漫角色,角色资料,风车动漫网",
  });

  const state = useCategoryFilter<Character>(
    characterList,
    (character) => ({ anime: [character.animeTitle], roleType: [character.roleType] }),
    sortOptions,
    8,
  );

  return (
    <Layout>
      <CategoryShell
        crumbs={[{ label: "角色介绍" }]}
        eyebrow="Characters"
        title="角色介绍"
        icon="ri-user-star-line"
        description="每一个让人记住的角色，都有值得细说的背景与羁绊。按作品或角色属性筛选，认识他们背后的故事。"
        filterGroups={filterGroups}
        selected={state.selected}
        onToggle={state.toggle}
        onReset={state.reset}
        sortOptions={sortOptions.map((option) => option.label)}
        sort={state.sort}
        onSort={state.setSort}
        resultLabel={`共 ${state.total} 位角色`}
      >
        {state.total === 0 ? (
          <EmptyState
            title="没有找到符合条件的角色"
            description="换个作品或属性试试，或重置筛选查看全部角色介绍。"
            action={
              <button
                type="button"
                onClick={state.reset}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
              {state.pageItems.map((character) => (
                <CharacterCard key={character.id} character={character} />
              ))}
            </div>
            <Pagination current={state.page} total={state.totalPages} onChange={state.setPage} />
          </>
        )}
      </CategoryShell>
    </Layout>
  );
}